import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LangProvider } from "./lib/LangContext";
import { Navigation } from "./components/site/Navigation";
import { CinematicHero } from "./components/home/CinematicHero";
import { StoryChapter } from "./components/home/StoryChapter";
import { ServicesChapter } from "./components/home/ServicesChapter";
import { PortfolioChapter, TrustChapter, ClubChapter, AcademyChapter } from "./components/home/TeaserChapters";
import { ChapterRail } from "./components/home/ChapterRail";
import { ServiceMapChapter } from "./components/home/ServiceMapChapter";
import { FAQ } from "./components/site/FAQ";
import { Newsletter } from "./components/site/Newsletter";
import { Contact } from "./components/site/Contact";
import { Footer } from "./components/site/Footer";
import { FloatingWhatsApp } from "./components/site/FloatingWhatsApp";
import { AssistantChat } from "./components/site/AssistantChat";
import { MobileStickyBar } from "./components/site/MobileStickyBar";
import { ClubPopup } from "./components/site/ClubPopup";
import { WhatsAppConsentGate } from "./components/site/WhatsAppConsentGate";
import { ScrollToTop } from "./components/site/ScrollToTop";
import { SEOHead } from "./components/site/SEOHead";
import { StudentAuthProvider } from "./lib/StudentAuth";
import { LegacyRedirect, LocaleLayout } from "./components/site/LangRouting";
import NotFound from "./pages/NotFound";

const LegalPage = lazy(() => import("./pages/LegalPage").then((m) => ({ default: m.LegalPage })));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminCRM = lazy(() => import("./pages/AdminCRM"));
const AdminPureGarageApps = lazy(() => import("./pages/AdminPureGarageApps"));
const AdminReset = lazy(() => import("./pages/AdminReset"));
const Showcase = lazy(() => import("./pages/Showcase"));
const Academy = lazy(() => import("./pages/Academy"));
const StudentLogin = lazy(() => import("./pages/StudentLogin"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const Works = lazy(() => import("./pages/Works"));
const PureGarage = lazy(() => import("./pages/PureGarage"));

const PageFallback = () => <div data-testid="page-loading" className="min-h-screen bg-[#050505]" />;

const LEGAL = ["impressum", "datenschutz", "agb", "widerruf", "cookies"];
const LEGACY_PATHS = ["/", "/arbeiten", "/academy", ...LEGAL.map((s) => `/${s}`)];

// P2.1 cinematic homepage: 10-chapter scroll journey (previous sections kept in components/site for reuse).
const Landing = () => (
  <main data-testid="home-cinematic">
    <CinematicHero />
    <StoryChapter id="experience" />
    <StoryChapter id="craft" />
    <StoryChapter id="transformation" />
    <ServicesChapter />
    <PortfolioChapter />
    <TrustChapter />
    <ServiceMapChapter />
    <ClubChapter />
    <AcademyChapter />
    <Newsletter />
    <FAQ />
    <div data-chapter="10"><Contact /></div>
    <ChapterRail />
  </main>
);

const Shell = ({ children }) => {
  const location = useLocation();
  const isPureGarageApp = location.pathname === "/app";
  return (
    <div data-testid="puregloss-app" className="min-h-screen bg-[#050505] text-white antialiased">
      <Navigation />
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
      {!isPureGarageApp && <Footer />}
      <FloatingWhatsApp />
      <AssistantChat />
      <MobileStickyBar />
      <ClubPopup />
      <WhatsAppConsentGate />
    </div>
  );
};

function App() {
  return (
    <LangProvider>
      <StudentAuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SEOHead />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            {LEGACY_PATHS.map((p) => <Route key={p} path={p} element={<LegacyRedirect />} />)}
            <Route path="/:lng" element={<LocaleLayout shell={Shell} />}>
              <Route index element={<Shell><Landing /></Shell>} />
              <Route path="arbeiten" element={<Shell><Works /></Shell>} />
              <Route path="academy" element={<Shell><Academy /></Shell>} />
              {LEGAL.map((s) => <Route key={s} path={s} element={<Shell><LegalPage section={s} /></Shell>} />)}
              <Route path="*" element={<Shell><NotFound /></Shell>} />
            </Route>
            <Route path="/app" element={<Shell><PureGarage /></Shell>} />
            <Route path="/academy/login" element={<StudentLogin />} />
            <Route path="/academy/dashboard" element={<StudentDashboard />} />
            <Route path="/admin-leads" element={<AdminLeads />} />
            <Route path="/admin-reset" element={<AdminReset />} />
            <Route path="/admin/crm" element={<AdminCRM />} />
            <Route path="/admin/puregarage-apps" element={<AdminPureGarageApps />} />
            <Route path="/showcase" element={<Showcase />} />
            <Route path="*" element={<Shell><NotFound /></Shell>} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </StudentAuthProvider>
    </LangProvider>
  );
}

export default App;
